import React, { useState } from "react";
import clsx from "clsx";
import styles from "./ComponentCard.module.css";
import ProductSelectionDrawer from "./ProductSelectionDrawer";
import ProductDetailsDrawer from "./ProductDetailsDrawer";
import { useViewModel } from "../../hooks/useViewModel.ts";
import type { ProductDto, ValidationStatus, RuleResult } from "../../types/validation";
import { ConfiguratorStringsContext } from "../../context/ConfiguratorStringsContext.ts";
import type { SharedCardRenderProps } from "./ComponentCard.types.ts";
import SingleSelectionCardContent from "./SingleSelectionCardContent.tsx";
import MultipleSelectionCardContent from "./MultipleSelectionCardContent.tsx";

export { PerformanceBar } from "./PerformanceBar.tsx";
export type { PerformanceBarProps } from "./PerformanceBar.tsx";
export { PriceDifferenceBadge } from "./PriceDifferenceBadge.tsx";
export type { PriceDifferenceBadgeProps } from "./PriceDifferenceBadge.tsx";

interface ComponentCardProps {
	kind: string;
	title?: string;
	meta?: string[];
	description?: string;
	imageUrl?: string;
	stockStatus?: {
		iconClass?: string;
		description?: string;
	};
	onReadMoreClick?: () => void;
	onSelectClick?: () => void;
	selectButtonText?: string;
	isInDrawer?: boolean;
	children?: React.ReactNode;
	configBundleId?: string;
	productGroupId?: string;
	materialNumber?: string;
	onProductSelect?: (
		productGroupId: string,
		productId: string | null,
		productData?: any,
	) => void;
	selectedProductId?: string | null;
	defaultProductName?: string | null;
	isCustomized?: boolean;
	isSelected?: boolean;
	forcedDefault?: boolean;
	isEmpty?: boolean;
	canSelectEmpty?: boolean;
	defaultProductId?: string;
	validationStatus?: ValidationStatus;
	validationErrors?: RuleResult[];
	currentConfiguration?: ProductDto[];
	isReplaced?: boolean;
	priceDifference?: {
		net: number;
		gross: number;
	};
	performanceComponentType?: string | null;
	isDeselectable?: boolean;
	multipleSelectionItems?: Array<{
		id: string;
		title?: string;
		imageUrl?: string;
		stockStatus?: { iconClass?: string; description?: string };
	}>;
	isMultipleSelection?: boolean;
}


const ComponentCard: React.FC<ComponentCardProps> = ({
	kind,
	title,
	meta,
	description,
	imageUrl,
	stockStatus,
	onReadMoreClick,
	onSelectClick,
	isInDrawer = false,
	children,
	configBundleId,
	productGroupId,
	materialNumber,
	onProductSelect,
	selectedProductId,
	defaultProductName,
	isCustomized = false,
	isSelected = false,
	forcedDefault = false,
	isEmpty = false,
	canSelectEmpty = false,
	defaultProductId,
	validationStatus,
	validationErrors,
	currentConfiguration,
	priceDifference,
	isReplaced = false,
	performanceComponentType = null,
	selectButtonText,
	multipleSelectionItems,
	isMultipleSelection = false,
	isDeselectable = false,
}) => {
	const [isSelectionDrawerOpen, setIsSelectionDrawerOpen] = useState(false);
	const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);

	const handleReadMoreClick =
		onReadMoreClick || (() => setIsDetailsDrawerOpen(true));
	const handleSelectClick =
		onSelectClick || (() => setIsSelectionDrawerOpen(true));

	const { data } = useViewModel();
	const { strings } = data!;

	const isEmptyProduct = isEmpty || !title || !materialNumber;

	const displayTitle = isEmptyProduct
		? strings.product.configBundles?.notSelected?.webText1
		: title;
	const displayDescription = isEmptyProduct
		? strings.product.configBundles?.notSelected?.webText2
		: description;
	const displayMeta = isEmptyProduct ? [] : meta || [];

	const buttonText = (() => {
		if (isEmptyProduct)
			return strings.product.configBundles.notSelected.notChosen;
		if (forcedDefault)
			return strings.product.configBundles?.defaultSelectedForcedDefault;
		if (isInDrawer && isSelected) return strings.product.configBundles.selected;
		return selectButtonText || strings.product.configBundles.edit;
	})();

	const isButtonDisabled = (isSelected && !isDeselectable) || forcedDefault;

	const sharedProps: SharedCardRenderProps = {
		kind,
		isCustomized,
		isReplaced,
		validationStatus,
		validationErrors,
		children,
		productGroupId,
		onSelectClick: handleSelectClick,
		onReadMoreClick: handleReadMoreClick,
	};

	return (
		<ConfiguratorStringsContext.Provider value={strings}>
			<article
				className={clsx(styles.card, {
					[styles.cardSelected]: isSelected && isInDrawer,
				})}
				aria-current={isSelected && isInDrawer ? "true" : undefined}
			>
				{isMultipleSelection ? (
					<MultipleSelectionCardContent
						{...sharedProps}
						multipleSelectionItems={multipleSelectionItems}
					/>
				) : (
					<SingleSelectionCardContent
						{...sharedProps}
						isInDrawer={isInDrawer}
						imageUrl={imageUrl}
						isEmptyProduct={isEmptyProduct}
						displayTitle={displayTitle}
						displayDescription={displayDescription}
						displayMeta={displayMeta}
						stockStatus={stockStatus}
						materialNumber={materialNumber}
						priceDifference={priceDifference}
						isButtonDisabled={isButtonDisabled}
						buttonText={buttonText}
					/>
				)}
			</article>

			{!isInDrawer && (
				<>
					<ProductSelectionDrawer
						isOpen={isSelectionDrawerOpen}
						onClose={() => setIsSelectionDrawerOpen(false)}
						productKind={kind}
						performanceComponentType={performanceComponentType}
						configBundleId={configBundleId!}
						productGroupId={productGroupId!}
						currentProductId={selectedProductId ?? materialNumber ?? null}
						defaultProductName={defaultProductName}
						onProductSelect={onProductSelect}
						canSelectEmpty={canSelectEmpty}
						defaultProductId={defaultProductId}
						currentConfiguration={currentConfiguration}
						strings={strings}
						isMultipleSelection={isMultipleSelection}
						selectedProductIds={
							isMultipleSelection
								? (multipleSelectionItems ?? []).map((i) => i.id)
								: undefined
						}
					/>
					{!isEmptyProduct && (
						<ProductDetailsDrawer
							isOpen={isDetailsDrawerOpen}
							onClose={() => setIsDetailsDrawerOpen(false)}
							product={{
								materialNumber: materialNumber,
								kind,
								title: displayTitle!,
								meta: displayMeta,
								description: displayDescription!,
								imageUrl: imageUrl || "",
								stockStatus,
								priceDifference,
								manufacturer: undefined,
							}}
						/>
					)}
				</>
			)}
		</ConfiguratorStringsContext.Provider>
	);
};

export default ComponentCard;